import { FieldLayerWellsGroup, LayerInfo } from '@/api/fields';
import { deleteWell } from '@/api/wells';
import { countFieldWells, flattenFieldWells } from '@/utils/fieldWells';
import { formatWellDate } from '@/utils/wellDates';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CreateWellModal } from './CreateWellModal';
import { WellDetailModal } from './WellDetailModal';

interface WellsListProps {
  wells: FieldLayerWellsGroup[];
  layers?: LayerInfo[];
  fieldId: string;
  canManage?: boolean;
  onWellsUpdated?: () => void;
}

export const WellsList: React.FC<WellsListProps> = ({
  wells,
  layers = [],
  fieldId,
  canManage = false,
  onWellsUpdated,
}) => {
  const [selectedWellId, setSelectedWellId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [wellToDelete, setWellToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const layerNames = useMemo(
    () => Object.fromEntries(layers.map((layer) => [layer.id, layer.name])),
    [layers]
  );

  const flatWells = useMemo(
    () =>
      flattenFieldWells(wells).map((well) => ({
        ...well,
        layer: layerNames[well.layer_id] ?? null,
      })),
    [wells, layerNames]
  );

  const handleWellClick = (wellId: string) => {
    setSelectedWellId(wellId);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedWellId(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleDeleteClick = (well: { id: string; name: string }) => {
    setWellToDelete(well);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!wellToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteWell(wellToDelete.id);
      setDeleteDialogOpen(false);
      setWellToDelete(null);
      onWellsUpdated?.();
    } catch (err) {
      console.error('Failed to delete well:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'production':
        return 'success';
      case 'injection':
        return 'info';
      case 'investigation':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = formatWellDate;

  const filteredWells = useMemo(() => {
    if (!searchTerm.trim()) {
      return flatWells;
    }
    const searchLower = searchTerm.toLowerCase();
    return flatWells.filter(
      (well) =>
        well.name.toLowerCase().includes(searchLower) ||
        (well.layer && well.layer.toLowerCase().includes(searchLower)) ||
        well.type.toLowerCase().includes(searchLower)
    );
  }, [flatWells, searchTerm]);

  const totalWells = countFieldWells(wells);

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        TransitionProps={{ timeout: 100 }}
        sx={{
          '& .MuiAccordionDetails-root': {
            transition: 'none',
          },
          '& .MuiCollapse-root': {
            transition: 'height 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="wells-content"
          id="wells-header"
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={2}>
            <Typography variant="h6">Wells ({totalWells})</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
              <TextField
                placeholder="Search by name, layer, or type..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ flexGrow: 1 }}
              />
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Add Well
                </Button>
              )}
            </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Layer</TableCell>
                  <TableCell>Entry Date</TableCell>
                  <TableCell>Shut Down Date</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWells.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? 6 : 5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm ? 'No wells found matching your search' : 'No wells found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredWells.map((well) => (
                    <TableRow key={well.id}>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleWellClick(well.id)}
                          sx={{ textDecoration: 'none', cursor: 'pointer' }}
                        >
                          {well.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={well.type}
                          color={getTypeColor(well.type) as 'success' | 'info' | 'warning' | 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {well.layer || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(well.entry_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(well.shut_down_date)}
                        </Typography>
                      </TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <Tooltip title="Delete well">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(well)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      <WellDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        wellId={selectedWellId}
        canManage={canManage}
        onWellUpdated={onWellsUpdated}
      />

      <CreateWellModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        fieldId={fieldId}
        onWellCreated={onWellsUpdated}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete well?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete &quot;{wellToDelete?.name}&quot; and all associated historical data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
